import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Search, Edit, Trash2, MapPin } from "lucide-react";
import {
  rackLocationService,
  RackLocation,
} from "../../services/rack-location.service";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

export default function RackLocationsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    name: string;
  }>({
    isOpen: false,
    id: "",
    name: "",
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["rack-locations", { search, page }],
    queryFn: () => rackLocationService.getAll({ search, page, limit: 20 }),
  });

  const deleteMutation = useMutation({
    mutationFn: rackLocationService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rack-locations"] });
    },
  });

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteModal({ isOpen: true, id, name });
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(deleteModal.id);
    setDeleteModal({ isOpen: false, id: "", name: "" });
  };

  const locations: RackLocation[] = data?.data || [];

  // Summary stats
  const totalLocations = data?.pagination?.total || locations.length;
  const activeLocations = locations.filter((l) => l.isActive).length;
  const totalBatches = locations.reduce(
    (sum, l) => sum + (l._count?.batchMappings || 0),
    0,
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6" />
            Rack Locations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage storage locations and track batch placement
          </p>
        </div>
        <Link
          to="/rack-locations/create"
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Location
        </Link>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Total Locations</div>
          <div className="text-2xl font-bold text-gray-900">
            {totalLocations}
          </div>
          <div className="text-xs text-green-600 mt-1">
            {activeLocations} active
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Batches Stored</div>
          <div className="text-2xl font-bold text-gray-900">{totalBatches}</div>
          <div className="text-xs text-gray-500 mt-1">Across all locations</div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Utilization</div>
          <div className="text-2xl font-bold text-gray-900">
            {totalLocations > 0
              ? `${Math.round((locations.filter((l) => (l._count?.batchMappings || 0) > 0).length / locations.length) * 100)}%`
              : "0%"}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Locations with batches
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code, name, or zone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : locations.length === 0 ? (
          <div className="p-12 text-center">
            <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-2">No rack locations found</p>
            <p className="text-sm text-gray-400">
              {search
                ? "Try a different search term"
                : "Create your first rack location to start organizing inventory"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Zone
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Batches
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {locations.map((location: RackLocation) => {
                  const batchCount = location._count?.batchMappings || 0;
                  return (
                    <tr key={location.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {location.rackName}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {location.rackCode}
                        </div>
                        {location.store?.name && (
                          <div className="text-xs text-gray-400">
                            {location.store.name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {location.zone || (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {location.rackType && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {location.rackType}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-semibold ${batchCount > 0 ? "text-green-700" : "text-gray-400"}`}
                          >
                            {batchCount}
                          </span>
                          {location.capacity && location.capacity > 0 && (
                            <div className="flex-1 max-w-[80px]">
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full ${
                                    batchCount / location.capacity > 0.9
                                      ? "bg-red-500"
                                      : batchCount / location.capacity > 0.6
                                        ? "bg-yellow-500"
                                        : "bg-green-500"
                                  }`}
                                  style={{
                                    width: `${Math.min(100, (batchCount / location.capacity) * 100)}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            location.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {location.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/rack-locations/${location.id}/edit`}
                            className="p-1 text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() =>
                              handleDeleteClick(location.id, location.rackName)
                            }
                            className="p-1 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data?.pagination && data.pagination.totalPages > 1 && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {(page - 1) * 20 + 1} to{" "}
              {Math.min(page * 20, data.pagination.total)} of{" "}
              {data.pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(data.pagination.totalPages, p + 1))
                }
                disabled={page === data.pagination.totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: "", name: "" })}
        onConfirm={handleDeleteConfirm}
        title="Delete Rack Location"
        message={`Are you sure you want to delete "${deleteModal.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
